import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnTab from "@shared/components/SnTab";
import SnButton from "@shared/components/SnButton";
import { global } from "@shared/libs/Global";
import SnMemoBox from "@shared/components/SnMemoBox";
import { menuInfo } from "@shared/type/CommonType";
import { getScript } from "@shared/utils/IssueUtils";
import { callApi, fnSave } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import { showIssueModal } from "@widgets/IssueModal";
import SN_SFA_ISSUE_REG_002_POP_004 from "./SN_SFA_ISSUE_REG_002_POP_004";
import SnGrid from "@shared/components/Grid/SnGrid";
import { getInitPopUpData } from "@shared/utils/CommonUtils";

interface popupProps {
  formData: any;
  modalRef?: any;
  Menuprops: menuInfo;
  scriptList?: any[];
  /** 제품구분(SFA043) dc_ref. DB 종류(예: "MSSQL"). 부모 화면에서 조회해서 넘김 */
  sqlType?: string;
}

/* DB SCRIPT 팝업 */
const SN_SFA_ISSUE_REG_002_POP_003 = forwardRef(
  (
    { formData, modalRef, Menuprops, scriptList, sqlType }: popupProps,
    ref: React.Ref<any>
  ) => {
    const { userInfo } = global();

    // 제품구분의 DB 종류(sqlType)가 MSSQL이면 sys_query / menu_id 탭 숨김
    const isMssql =
      String(sqlType ?? "")
        .trim()
        .toUpperCase() === "MSSQL";

    // 초기 조회 내용
    const initB = useRef("");
    const initP = useRef("");
    const initR = useRef("");
    const initS = useRef([]);
    const initC = useRef([]);

    // 적용 여부
    const applyB = useRef("N");
    const applyP = useRef("N");
    const applyR = useRef("N");
    const applyS = useRef("N");
    const applyC = useRef("N");

    // execute 실행 여부
    let checked_B = useRef("N");
    let checked_P = useRef("N");
    let checked_R = useRef("N");
    let checked_S = useRef("N");

    const [activeTab, setActiveTab] = useState("1");

    const [scriptB, setScriptB] = useState("");
    const [scriptP, setScriptP] = useState("");
    const [scriptR, setScriptR] = useState("");
    const [scriptS, setScriptS] = useState("");

    /* scriptS */
    const gridref1 = useRef<any>(null);
    const [columns1, setColums1] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any>();

    /* scriptC */
    const gridref2 = useRef<any>(null);
    const [columns2, setColums2] = useState<any[]>([]);
    const [dataSource2, setDataSource2] = useState<any>();

    const [gridFocus, setGridFocus] = useState(false);
    const [focusedRowData, setFocusedRowData] = useState<any>();

    useImperativeHandle(ref, () => ({
      onSave,
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_SFA_ISSUE_REG_002_POP_003"
          );
          setColums1(rtn.grid.grid1);
          setColums2(rtn.grid.grid2);
          setTimeout(() => setGridFocus(true), 50);
          gridref1.current?.resetUpdate();
          gridref2.current?.resetUpdate();
        } catch (error) {}
      };
      fetchform();
    }, [userInfo]);

    useEffect(() => {
      gridref1.current?.selectAndFocusRow(0);
      setGridFocus(false);
    }, [gridFocus]);

    useEffect(() => {
      if (scriptList && Array.isArray(scriptList)) {
        const scriptBObj = scriptList.find((item) => item.fg_script === "B");
        initB.current = scriptBObj?.txt_script ?? "";
        setScriptB(scriptBObj?.txt_script ?? "");
        applyB.current = scriptBObj?.yn_apply ?? "N";

        const scriptPObj = scriptList.find((item) => item.fg_script === "P");
        initP.current = scriptPObj?.txt_script ?? "";
        setScriptP(scriptPObj?.txt_script ?? "");
        applyP.current = scriptPObj?.yn_apply ?? "N";

        const scriptRObj = scriptList.find((item) => item.fg_script === "R");
        initR.current = scriptRObj?.txt_script ?? "";
        setScriptR(scriptRObj?.txt_script ?? "");
        applyR.current = scriptRObj?.yn_apply ?? "N";

        const scriptSObj = scriptList.find((item) => item.fg_script === "S");
        const list = scriptSObj?.txt_script
          ? JSON.parse(scriptSObj.txt_script)
          : [];
        initS.current = list;
        setScriptS(list[0]?.txt_script || "");
        setDataSource1(list);
        applyS.current = scriptSObj?.yn_apply ?? "N";

        const scriptCObj = scriptList.find((item) => item.fg_script === "C");
        const cList = scriptCObj?.txt_script
          ? JSON.parse(scriptCObj.txt_script)
          : [];
        initC.current = cList;
        setDataSource2(cList);
        applyC.current = scriptCObj?.yn_apply ?? "N";
      }
    }, [scriptList]);

    // 쿼리 ID(sys_query의 cd_query)가 Confirm/Cancel 되지 않은 다른 이슈에 등록되어 있으면 통제
    const checkSysQuery = async (rows: any[]) => {
      const cd_query = rows.map((row) => row.cd_query).filter(Boolean);
      if (cd_query.length === 0) return true;

      const dup = cd_query.filter((q, i) => cd_query.indexOf(q) !== i);
      if (dup.length > 0) {
        await showDialog(
          "alter",
          "중복된 쿼리 ID가 있습니다.\n" + Array.from(new Set(dup)).join("\n")
        );
        return false;
      }

      const result = await callApi("checkSysQuery", {
        cd_firm: userInfo.cd_firm,
        no_issue: formData.no_issue,
        cd_query,
      });
      if (!result?.status) return false;
      if (result.data.length > 0) {
        const list = result.data
          .map((d: any) => `[${d.no_issue}]${d.dc_title}`)
          .join("\n");
        await showDialog(
          "alter",
          "다른 이슈에 등록된 쿼리 ID가 있습니다.\n\n" + list
        );
        return false;
      }
      return true;
    };

    const onSave = async () => {
      if (!isMssql) {
        gridref1.current?.api.stopEditing();
        const rowData1: any[] = [];
        gridref1.current?.api?.forEachNode((node: any) => {
          if (node.data)
            rowData1.push({
              cd_query: node.data.cd_query,
              dc_query: node.data.dc_query || "",
              dc_rmk: node.data.dc_rmk || "",
            });
        });

        gridref2.current?.api.stopEditing();
        const rowData2: any[] = [];
        gridref2.current?.api?.forEachNode((node: any) => {
          if (node.data)
            rowData2.push({
              nm_menu: node.data.nm_menu,
              dc_rmk: node.data.dc_rmk || "",
            });
        });

        if (
          initB.current === scriptB &&
          initP.current === scriptP &&
          initR.current === scriptR &&
          (gridref1.current?.getChange().modifiedRowcount ?? 0) === 0 &&
          (gridref2.current?.getChange().modifiedRowcount ?? 0) === 0 &&
          checked_S.current === "N"
        ) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }

        if (
          checked_B.current === "Y" ||
          checked_P.current === "Y" ||
          checked_R.current === "Y" ||
          checked_S.current === "Y"
        ) {
          await showDialog("alter", "Please execute the Script before saving.");
          return false;
        }

        // 필수값 체크
        if (!gridref1.current?.requiredValuesCheck()) {
          return;
        }
        if (!gridref2.current?.requiredValuesCheck()) {
          return;
        }

        if (!(await checkSysQuery(rowData1))) {
          return false;
        }

        const updateData: any = {
          cd_firm: userInfo.cd_firm,
          no_issue: formData.no_issue,
          tm_amd: formData.tm_amd,
          cd_user_amd: userInfo.cd_user,
          nm_user_amd: userInfo.nm_user,
        };

        // js 데이터 추가
        const js_script = getScript(
          scriptB,
          scriptP,
          scriptR,
          rowData1,
          rowData2,
          scriptList
        );
        if (js_script) updateData.js_script = js_script;

        const saveData = {
          sfa_issue: {
            update: [updateData],
          },
        };

        try {
          await fnSave("SN_SFA_ISSUE_REP_001_btnNewIssue", saveData);
          return true;
        } catch (err) {
          return false;
        }
      } else {
        if (
          checked_B.current === "Y" ||
          checked_P.current === "Y" ||
          checked_R.current === "Y"
        ) {
          await showDialog("alter", "Please execute the Script before saving.");
          return false;
        }

        if (
          initB.current === scriptB &&
          initP.current === scriptP &&
          initR.current === scriptR
        ) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return false;
        }

        // MSSQL 제품은 sys_query/menu_id(S/C) 탭이 없으므로 B/P/R 만 만든다
        const updateData: any = {
          cd_firm: userInfo.cd_firm,
          no_issue: formData.no_issue,
          tm_amd: formData.tm_amd,
          cd_user_amd: userInfo.cd_user,
          nm_user_amd: userInfo.nm_user,
        };

        const js_script = getScript(
          scriptB,
          scriptP,
          scriptR,
          [],
          [],
          scriptList
        );
        if (js_script) updateData.js_script = js_script;

        try {
          await fnSave("SN_SFA_ISSUE_REP_001_btnNewIssue", {
            sfa_issue: { update: [updateData] },
          });
          return true;
        } catch (err) {
          return false;
        }
      }
    };

    const onTopBtnsClick = async (id: string) => {
      if (id === "help") {
        let fg_script = "";
        let title = "";
        switch (activeTab) {
          case "1":
            fg_script = "B";
            title = "Table Creation Example";
            break;
          case "2":
            fg_script = "P";
            title = "Procedure, Function, View Creation Example";
            break;
          case "3":
            fg_script = "R";
            title = "Trigger Creation Example";
            break;
        }

        const component = (
          <SN_SFA_ISSUE_REG_002_POP_004
            fg_script={fg_script}
            sqlType={sqlType}
          />
        );
        const result = await showIssueModal({
          title: title,
          children: component,
          width: "40%",
          height: "80%",
          okayBtn: false,
        });
      } else if (id === "execute") {
        if (!isMssql) {
          if (
            applyB.current === "Y" ||
            applyP.current === "Y" ||
            applyR.current === "Y" ||
            applyS.current === "Y"
          ) {
            await showDialog("alter", "적용완료된 스크립트가 존재합니다.");
            return;
          }

          let str_query = "";
          let fg_script = "";
          switch (activeTab) {
            case "1":
              str_query = scriptB;
              fg_script = "B";
              break;
            case "2":
              str_query = scriptP;
              fg_script = "P";
              break;
            case "3":
              str_query = scriptR;
              fg_script = "R";
              break;
            case "4":
              gridref1.current?.api?.forEachNode(async (node: any) => {
                if (node.data) str_query = str_query + node.data.dc_query;
              });
              fg_script = "S";
              break;
          }

          if (activeTab !== "4" && activeTab !== "5" && !str_query) {
            await showDialog("alter", "Please make a DB Script first.");
            return;
          }

          if (
            activeTab !== "4" &&
            activeTab !== "5" &&
            !str_query.includes("IF EXISTS") &&
            !str_query.includes("IF NOT EXISTS")
          ) {
            await showDialog(
              "alter",
              "Please make a DB Script using 'IF EXISTS', 'IF NOT EXISTS'.\r\n If you don't know Press 'Help' Button"
            );
            return;
          }

          const lines = str_query
            .split(/\r?\n/)
            .filter((line) => line.trim() !== "");
          const lastLine = lines[lines.length - 1] ?? "";
          const trimmedLastLine = lastLine.trim();

          if (activeTab !== "5") {
            const endsWithSemicolon = trimmedLastLine.endsWith(";");
            if (!endsWithSemicolon) {
              await showDialog(
                "alter",
                "You should add the semicolon(;) below every paragraph."
              );
              return;
            }
          }

          if (activeTab === "4") {
            const rows: any[] = [];
            gridref1.current?.api?.forEachNode((node: any) => {
              if (node.data) rows.push(node.data);
            });
            if (!(await checkSysQuery(rows))) return;
          }

          const params = {
            query: str_query,
            fg_script: fg_script,
          };

          const result = await callApi("executeScript/do", params);
          if (result.status) {
            switch (activeTab) {
              case "1":
                checked_B.current = "N";
                break;
              case "2":
                checked_P.current = "N";
                break;
              case "3":
                checked_R.current = "N";
                break;
              case "4":
                checked_S.current = "N";
                break;
            }
          }
        } else {
          let txtScript = "";
          let fgScript = "";

          switch (activeTab) {
            case "1":
              txtScript = scriptB;
              fgScript = "B";
              break;
            case "2":
              txtScript = scriptP;
              fgScript = "P";
              break;
            case "3":
              txtScript = scriptR;
              fgScript = "R";
              break;
          }

          if (!txtScript || txtScript.trim() === "") {
            await showDialog("alter", "Please make a DB Script first.");
            return;
          }

          if (
            (!txtScript.includes("IF EXISTS") &&
              !txtScript.includes("IF NOT EXISTS")) ||
            !txtScript.includes("GO")
          ) {
            await showDialog(
              "alter",
              "Please make a DB Script using 'IF EXISTS', 'IF NOT EXISTS' and 'GO'.\r\n If you don't know Press 'Help' Button"
            );
            return;
          }
          let sqlBatch = "";
          for (const line of txtScript.split(/\r?\n/)) {
            if (line.trim() === "") continue;
            if (line.trim().toUpperCase() === "GO") {
              sqlBatch = "";
            } else {
              sqlBatch += line + "\n";
            }
          }
          if (sqlBatch !== "") {
            await showDialog(
              "alter",
              "You should add the 'GO' below every paragraph."
            );
            return;
          }

          const result = await callApi("executeScript/doDotNet", {
            query: txtScript,
          });

          if (result?.status) {
            switch (activeTab) {
              case "1":
                checked_B.current = "N";
                break;
              case "2":
                checked_P.current = "N";
                break;
              case "3":
                checked_R.current = "N";
                break;
            }
          }
        }
      } else if (id === "clear") {
        if (
          applyB.current === "Y" ||
          applyP.current === "Y" ||
          applyR.current === "Y"
        ) {
          await showDialog("alter", "적용완료된 스크립트가 존재합니다.");
          return;
        }

        switch (activeTab) {
          case "1":
            setScriptB("");
            checked_B.current = "N";
            break;
          case "2":
            setScriptP("");
            checked_P.current = "N";
            break;
          case "3":
            setScriptR("");
            checked_R.current = "N";
            break;
        }
      }
    };

    const onValueChanged = (fg_script: string, txt_script: string) => {
      switch (fg_script) {
        case "B":
          setScriptB(txt_script);
          if (initB.current !== txt_script && txt_script !== "")
            checked_B.current = "Y";
          break;
        case "P":
          setScriptP(txt_script);
          if (initP.current !== txt_script && txt_script !== "")
            checked_P.current = "Y";
          break;
        case "R":
          setScriptR(txt_script);
          if (initR.current !== txt_script && txt_script !== "")
            checked_R.current = "Y";
          break;
        case "S":
          setScriptS(txt_script);

          const matchedItem = initS.current.find(
            (item) =>
              (item as { cd_query: string; dc_query: string }).cd_query ===
              focusedRowData.cd_query
          );

          if (matchedItem) {
            // 기존 데이터
            const dc_query = (matchedItem as { dc_query: string }).dc_query;
            if (dc_query === txt_script || txt_script === "") {
              checked_S.current = "N";
              gridref1.current.sharedCellValueChanged({
                ...focusedRowData,
                ...{ dc_query: txt_script },
              });
              return;
            }
          }

          checked_S.current = "Y";
          gridref1.current.sharedCellValueChanged({
            ...focusedRowData,
            ...{ dc_query: txt_script },
          });
          break;
        case "C":
          break;
      }
    };

    const onHeaderBtnsClick = async (id: string) => {
      if (id == "addRow") {
        onAddRow();
      }
      if (id == "deleteRow") {
        onDeleteRow();
      }
    };

    const onAddRow = async () => {
      try {
        const newRow = {}; // 빈 객체
        if (activeTab === "4") {
          gridref1.current?.addRow(newRow);
        } else if (activeTab === "5") {
          gridref2.current?.addRow(newRow);
        }
      } catch (error: any) {}
    };

    const onDeleteRow = async () => {
      let targetGridRef = null;

      if (activeTab === "4") {
        targetGridRef = gridref1;
      } else if (activeTab === "5") {
        targetGridRef = gridref2;
      }

      const rowCount = targetGridRef?.current?.api?.getDisplayedRowCount();
      if (rowCount < 1) {
        return;
      }

      targetGridRef?.current?.deleteSelectedRows();
      if (rowCount == 1) {
        setScriptS("");
      }
    };

    const onFocusedRowChanged = useCallback(
      (focusedRowData: any | null) => {
        setFocusedRowData(focusedRowData);
        setScriptS(focusedRowData.dc_query || "");
      },
      [focusedRowData]
    );

    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
            height: "30px",
          }}
        >
          {!["4", "5"].includes(activeTab) && (
            <SnButton text={"Help"} onClick={() => onTopBtnsClick("help")} />
          )}
          {activeTab !== "5" && (
            <SnButton
              text={"Execute(F5)"}
              onClick={() => onTopBtnsClick("execute")}
            ></SnButton>
          )}
          {activeTab !== "5" && (
            <SnButton
              text={"Clear"}
              onClick={() => onTopBtnsClick("clear")}
            ></SnButton>
          )}
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SnTab
            titles={[
              "Table or Column",
              "Procedure, Function, View",
              "Trigger",
              ...(!isMssql ? ["sys_query", "menu_id"] : []),
            ]}
            width={"100%"}
            header={[
              <SnTabHeader />,
              <SnTabHeader />,
              <SnTabHeader />,
              ...(!isMssql
                ? [
                    <SnTabHeader
                      onBtnAddClick={onHeaderBtnsClick}
                      onBtnDeleteClick={onHeaderBtnsClick}
                    />,
                    <SnTabHeader
                      onBtnAddClick={onHeaderBtnsClick}
                      onBtnDeleteClick={onHeaderBtnsClick}
                    />,
                  ]
                : []),
            ]}
            onTabChanged={(index) => {
              setActiveTab(index);
            }}
            body={[
              <SnMemoBox
                defaultValue={scriptB}
                onValueChanged={(value: string) => {
                  onValueChanged("B", value);
                }}
                isReadOnly={applyB.current === "Y" || false}
              />,
              <SnMemoBox
                defaultValue={scriptP}
                onValueChanged={(value: string) => {
                  onValueChanged("P", value);
                }}
                isReadOnly={applyP.current === "Y" || false}
              />,
              <SnMemoBox
                defaultValue={scriptR}
                onValueChanged={(value: string) => {
                  onValueChanged("R", value);
                }}
                isReadOnly={applyR.current === "Y" || false}
              />,
              ...(!isMssql
                ? [
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "row",
                        gap: 5,
                      }}
                    >
                      <div style={{ height: "100%", width: "40%" }}>
                        <SnGrid
                          seq={1}
                          ref={gridref1}
                          columnConfig={columns1}
                          rowData={dataSource1}
                          isEditable={true}
                          rowSelection="single"
                          enableAddRow={true}
                          onBtnAddClick={onHeaderBtnsClick}
                          onBtnDeleteClick={onHeaderBtnsClick}
                          onFocusedRowChanged={onFocusedRowChanged}
                        />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          height: "100%",
                          width: "60%",
                        }}
                      >
                        <SnMemoBox
                          defaultValue={scriptS}
                          onValueChanged={(value: string) => {
                            onValueChanged("S", value);
                          }}
                          isReadOnly={applyS.current === "Y" || false}
                        ></SnMemoBox>
                      </div>
                    </div>,
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "row",
                        gap: 5,
                      }}
                    >
                      <div style={{ height: "100%", width: "100%" }}>
                        <SnGrid
                          seq={1}
                          ref={gridref2}
                          columnConfig={columns2}
                          rowData={dataSource2}
                          isEditable={true}
                          rowSelection="single"
                          enableAddRow={true}
                          onBtnAddClick={onHeaderBtnsClick}
                          onBtnDeleteClick={onHeaderBtnsClick}
                          onFocusedRowChanged={onFocusedRowChanged}
                        />
                      </div>
                    </div>,
                  ]
                : []),
            ]}
          />
        </div>
      </div>
    );
  }
);

export default SN_SFA_ISSUE_REG_002_POP_003;
